/**
 * ProformaNavigationGuardContext
 *
 * Lightweight context that tracks whether a proforma module page (CI, SSI,
 * DD, PAD) has unsaved changes that would require AI paragraph regeneration.
 * Breadcrumb and TopNavBar consume `guardedNavigate` instead of calling
 * `navigate()` directly; when the active page is dirty the user sees a
 * confirmation dialog before navigation proceeds.
 *
 * The context is designed to be zero-overhead for non-module pages: `isDirty`
 * defaults to `false`, and `guardedNavigate` falls straight through to
 * React Router's `navigate()`.
 *
 * Module pages never interact with this context directly — they use the
 * `useSectionExitRegeneration` hook, which registers / clears dirty state
 * on their behalf.
 */
import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as AlertDialog from '@radix-ui/react-alert-dialog';

export const ProformaNavigationGuardContext = createContext({
  isDirty: false,
  sectionName: '',
  moduleCompletedOnce: false,
  registerDirty: () => {},
  clearDirty: () => {},
  guardedNavigate: () => {},
  registerExitInterceptor: () => {},
  clearExitInterceptor: () => {},
});

export function ProformaNavigationGuardProvider({ children }) {
  const navigate = useNavigate();

  const [sectionName, setSectionName] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  // Tracks whether the module that registered dirty state has been completed
  // at least once. When true, the confirmation dialog is shown on navigation;
  // when false (first-time walkthrough), navigation proceeds silently and
  // regeneration fires on unmount. This keeps the guard context module-agnostic
  // — the value is supplied by useSectionExitRegeneration, which derives it
  // from the patient data model for whichever module is active (CI, SSI, etc.).
  const [moduleCompletedOnce, setModuleCompletedOnce] = useState(false);

  // The display name of the active module (e.g. "Clinical Interview",
  // "Post-Assessment Discussion"). Used in the confirmation dialog title
  // so the clinician knows which module they are leaving. Supplied by
  // useSectionExitRegeneration via registerDirty's third argument.
  const [stageTitle, setStageTitle] = useState('');

  // When a guarded navigation is requested while dirty, we stash the
  // destination URL (and optional pre-navigation callback) here so the
  // confirmation dialog can act on it.
  const [pendingNav, setPendingNav] = useState(null);

  // Optional page-level exit interceptor. When registered, guardedNavigate
  // calls this function with the destination URL instead of showing the
  // default "leaving section" dialog. This lets pages like 1_DD.js run
  // their own save-and-confirm flow (e.g. saving criteria selections and
  // showing the completion dialog) before allowing navigation to proceed.
  const exitInterceptorRef = useRef(null);

  const registerExitInterceptor = useCallback((interceptor) => {
    exitInterceptorRef.current = interceptor;
  }, []);

  const clearExitInterceptor = useCallback(() => {
    exitInterceptorRef.current = null;
  }, []);

  // ─── API consumed by useSectionExitRegeneration ──────────────────
  const registerDirty = useCallback((name, completedOnce = false, stage = '') => {
    setSectionName(name);
    setIsDirty(true);
    setModuleCompletedOnce(completedOnce);
    setStageTitle(stage);
  }, []);

  const clearDirty = useCallback(() => {
    setIsDirty(false);
    setSectionName('');
    setModuleCompletedOnce(false);
    setStageTitle('');
  }, []);

  // ─── API consumed by Breadcrumb / TopNavBar ──────────────────────
  // `onConfirmCallback` is optional — used by logout to run signOut
  // before the actual navigation.
  //
  // Three branches:
  //  1. Dirty + module previously completed → show confirmation dialog
  //     so the clinician knows AI paragraphs will be regenerated and need
  //     re-review. This is the only path that shows the dialog.
  //  2. Dirty + first-time walkthrough + has callback (e.g. logout) →
  //     run the callback directly without a dialog. Regeneration still
  //     fires on unmount via useSectionExitRegeneration.
  //  3. Everything else (not dirty, or dirty first-time with no callback)
  //     → navigate directly. Regeneration fires on unmount if applicable.
  const guardedNavigate = useCallback((url, onConfirmCallback) => {
    // When a page-level exit interceptor is registered and the page is
    // dirty, delegate entirely to the interceptor. This lets the DD page
    // (for example) run its own save + confirmation flow rather than
    // showing the generic "leaving section" dialog.
    // Pass onConfirmCallback so the interceptor can invoke it (e.g. for
    // logout's signOut call) before or instead of standard navigation.
    if (isDirty && exitInterceptorRef.current) {
      exitInterceptorRef.current(url, onConfirmCallback);
      return;
    }

    if (isDirty && moduleCompletedOnce) {
      setPendingNav({ url, onConfirmCallback: onConfirmCallback || null });
    } else if (isDirty && onConfirmCallback) {
      onConfirmCallback();
    } else {
      navigate(url);
    }
  }, [isDirty, moduleCompletedOnce, navigate]);

  // ─── Dialog handlers ─────────────────────────────────────────────
  const handleConfirmLeave = useCallback(async () => {
    const nav = pendingNav;
    setPendingNav(null);
    clearDirty();
    if (nav?.onConfirmCallback) {
      await nav.onConfirmCallback();
    } else if (nav?.url) {
      navigate(nav.url);
    }
  }, [pendingNav, navigate, clearDirty]);

  const handleCancelLeave = useCallback(() => {
    setPendingNav(null);
  }, []);

  // ─── beforeunload for tab close / refresh ────────────────────────
  // Stash isDirty in a ref so the event handler always reads the
  // latest value without requiring the effect to re-run on every
  // isDirty toggle (avoids unnecessary add/remove listener churn).
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    const handler = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // ─── Context value (stable reference via useCallback above) ──────
  const value = {
    isDirty,
    sectionName,
    moduleCompletedOnce,
    registerDirty,
    clearDirty,
    guardedNavigate,
    registerExitInterceptor,
    clearExitInterceptor,
  };

  return (
    <ProformaNavigationGuardContext.Provider value={value}>
      {children}

      {/* Confirmation dialog — rendered at app level so it survives
          the module component unmount triggered by navigation. */}
      <AlertDialog.Root
        open={!!pendingNav}
        onOpenChange={(open) => { if (!open) handleCancelLeave(); }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-2xl shadow-lg max-w-md w-full z-50">
            <AlertDialog.Title className="text-lg font-bold text-gray-900">
              Leaving {stageTitle || 'this section'}
            </AlertDialog.Title>
            <AlertDialog.Description className="mt-3 text-sm text-gray-600">
              You&apos;ve made changes to <span className="font-semibold">{sectionName}</span>.
              If you leave now, the AI-generated draft content for this section will be
              regenerated in the background and will need to be re-reviewed in Manage Documents.
            </AlertDialog.Description>

            <div className="flex justify-end gap-3 mt-6">
              <AlertDialog.Cancel asChild>
                <button
                  className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Stay on Page
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  onClick={handleConfirmLeave}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-violet-900 text-white hover:bg-violet-800 transition-colors"
                >
                  Leave
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </ProformaNavigationGuardContext.Provider>
  );
}
